import { useRef, useCallback, useEffect } from 'react';
import { Network, DataSet } from 'vis-network/standalone';
import { MutableRefObject } from 'react';
import { Node } from '../types';

/**
 * Describes a node that needs to animate from one position to another
 * after expansion stabilization.
 */
export interface AnimatingNode {
  id: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  /** If true, the node was newly added (fade in + slide). Otherwise just slide. */
  isNew: boolean;
}

// ─── Animation timing ───────────────────────────────────────────────────────
const SLIDE_DURATION = 2000; // ms – total animation duration

// ─── Helpers ─────────────────────────────────────────────────────────────────
const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// ─── Hook ────────────────────────────────────────────────────────────────────
/**
 * Manages a smooth slide animation for nodes after expansion stabilization.
 *
 * After stabilization determines final positions, this hook:
 *  1. Snaps all affected nodes back to their pre-stabilization positions
 *  2. Smoothly animates them to their final (post-stabilization) positions
 *  3. New nodes also fade in during the animation
 *
 * Usage:
 *   1. Call `startSlideAnimation(nodes)` after stabilization finishes.
 *      The hook will snap nodes to `from` positions and animate to `to` positions.
 *   2. Call `cancelAnimation()` if you need to abort (e.g., another expansion starts).
 */
export const useExpansionSlideAnimation = (
  networkRef: MutableRefObject<Network | null>,
  graphNodes: DataSet<Node>,
  onAnimationComplete?: () => void
) => {
  const animationFrameRef = useRef<number | null>(null);
  const isAnimatingRef = useRef(false);
  const animatingNodesRef = useRef<AnimatingNode[]>([]);
  const startTimeRef = useRef<number | null>(null);

  // Store original node visuals so we can restore opacity after animation
  const originalStylesRef = useRef<Map<string, any>>(new Map());

  // ── Cancel any running animation ──────────────────────────────────────────
  const cancelAnimation = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    isAnimatingRef.current = false;
    animatingNodesRef.current = [];
    startTimeRef.current = null;
    originalStylesRef.current.clear();
  }, []);

  // ── Start the slide animation ─────────────────────────────────────────────
  const startSlideAnimation = useCallback((nodes: AnimatingNode[]) => {
    if (nodes.length === 0) return;

    // Cancel any in-flight animation
    cancelAnimation();

    const network = networkRef.current;
    if (!network) return;

    animatingNodesRef.current = nodes;
    isAnimatingRef.current = true;
    startTimeRef.current = Date.now();

    console.log(`✨ Starting expansion slide animation for ${nodes.length} node(s)`);

    // ── Step 1: Snap nodes to their FROM positions ──────────────────────
    // For new nodes: make them semi-transparent initially
    // For existing nodes: just move them back to pre-stabilization position
    const updates: any[] = [];
    nodes.forEach(({ id, fromX, fromY, isNew }) => {
      const existingNode = graphNodes.get(id) as any;
      if (!existingNode) return;

      // Store original style for restoration later
      originalStylesRef.current.set(id, {
        color: existingNode.color,
        font: existingNode.font,
        opacity: existingNode.opacity,
      });

      const update: any = {
        id,
        x: fromX,
        y: fromY,
        fixed: true, // Keep fixed during animation
      };

      if (isNew) {
        // Start new nodes as transparent — they'll fade in during animation
        update.opacity = 0;
      }

      updates.push(update);
    });

    if (updates.length > 0) {
      graphNodes.update(updates);
    }

    // ── Step 2: Animate with rAF ────────────────────────────────────────
    const tick = () => {
      if (!isAnimatingRef.current || !startTimeRef.current) return;
      if (networkRef.current !== network) {
        cancelAnimation();
        return;
      }

      const elapsed = Date.now() - startTimeRef.current;
      const rawProgress = Math.min(1, elapsed / SLIDE_DURATION);
      const easedProgress = easeInOutCubic(rawProgress);

      const positionUpdates: any[] = [];

      animatingNodesRef.current.forEach(({ id, fromX, fromY, toX, toY, isNew }) => {
        const currentX = fromX + (toX - fromX) * easedProgress;
        const currentY = fromY + (toY - fromY) * easedProgress;

        const update: any = { id, x: currentX, y: currentY };

        // Fade in new nodes
        if (isNew) {
          update.opacity = easedProgress;
        }

        positionUpdates.push(update);
      });

      if (positionUpdates.length > 0) {
        graphNodes.update(positionUpdates);
      }

      // Force network redraw to show updated positions
      network.redraw();

      if (rawProgress < 1) {
        animationFrameRef.current = requestAnimationFrame(tick);
      } else {
        // ── Animation complete ──────────────────────────────────────────
        console.log('✨ Expansion slide animation complete');

        // Restore full opacity and unlock nodes
        const finalUpdates: any[] = [];
        animatingNodesRef.current.forEach(({ id, toX, toY }) => {
          const original = originalStylesRef.current.get(id);
          finalUpdates.push({
            id,
            x: toX,
            y: toY,
            fixed: false,
            opacity: original?.opacity ?? 1,
          });
        });

        if (finalUpdates.length > 0) {
          graphNodes.update(finalUpdates);
        }

        // Clean up
        isAnimatingRef.current = false;
        animatingNodesRef.current = [];
        startTimeRef.current = null;
        originalStylesRef.current.clear();
        animationFrameRef.current = null;

        onAnimationComplete?.();
      }
    };

    animationFrameRef.current = requestAnimationFrame(tick);
  }, [networkRef, graphNodes, cancelAnimation, onAnimationComplete]);

  // ── Teardown ──────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      isAnimatingRef.current = false;
    };
  }, []);

  return { startSlideAnimation, cancelAnimation, isAnimating: isAnimatingRef };
};
